import { useRef, useState, useEffect, useCallback } from 'react'

interface UseHorizontalScrollOptions {
  scrollAmount?: number
  deps?: unknown[]
}

export function useHorizontalScroll({ scrollAmount = 200, deps = [] }: UseHorizontalScrollOptions = {}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const checkScrollPosition = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 1)
    setCanScrollRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 1)
  }, [])

  useEffect(() => {
    checkScrollPosition()
    const el = scrollRef.current
    if (!el) return

    el.addEventListener('scroll', checkScrollPosition)
    const resizeObserver = new ResizeObserver(checkScrollPosition)
    resizeObserver.observe(el)

    return () => {
      el.removeEventListener('scroll', checkScrollPosition)
      resizeObserver.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  const scrollBy = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTo({
      left: el.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount),
      behavior: 'smooth',
    })
  }, [scrollAmount])

  return { scrollRef, canScrollLeft, canScrollRight, scrollBy }
}
